import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Drawer, Skeleton } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityDetailLayout } from '../../../components/entity-detail/EntityDetailLayout.js';
import { EntityTabs } from '../../../components/entity-detail/EntityTabs.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { useWSChannel } from '../../../shared/ws-client/useWSChannel.js';
import { ErrorBoundary } from '../../../shared/ErrorBoundary.js';
import { DealChatPanel } from '../../ai-chat/components/ChatPanel.js';
import { useTasks } from '../../tasks/hooks/useTasks.js';
import { useNotes } from '../../notes/hooks/useNotes.js';
import { useMeetings } from '../../meetings/hooks/useMeetings.js';
import { useDocuments } from '../../documents/hooks/useDocuments.js';
import { useEntityFiles } from '../../contacts/hooks/useContactFiles.js';
import { EntityTasksTab } from '../../contacts/components/ContactTasksTab.js';
import { EntityMeetingsTab } from '../../contacts/components/ContactMeetingsTab.js';
import { EntityNotesTab } from '../../contacts/components/ContactNotesTab.js';
import { EntityDocumentsTab } from '../../contacts/components/ContactDocumentsTab.js';
import { EntityFilesTab } from '../../contacts/components/ContactFilesTab.js';
import { DealDetailHeader } from '../components/DealDetailHeader.js';
import { DealSidebar } from '../components/DealSidebar.js';
import { DealOverview } from '../components/DealOverview.js';
import { DealHistoryTab } from '../components/DealHistoryTab.js';
import { DealFormModal } from '../components/DealFormModal.js';
import { DealNextStepCard } from '../components/DealNextStepCard.js';
import { DealWhatIfSimulator } from '../components/DealWhatIfSimulator.js';
import { useDealHistory } from '../hooks/useDealHistory.js';
import {
  getDealById,
  getDealDetailQueryKey,
  getDealSalesHealth,
  getDealSalesHealthQueryKey,
  getDealWinProbability,
  getDealWinProbabilityQueryKey,
  type DealNextStepAction,
  type DealStageChangedEvent,
} from '../api/deals.client.js';
import '../components/deal-detail.css';

export interface DealCardPageProps {
  dealId: string;
}

export function DealCardPage({ dealId }: DealCardPageProps): ReactElement {
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const visit = `${location.key}:${dealId}`;
  const [chatState, setChatState] = useState({ visit, open: false });
  if (chatState.visit !== visit) setChatState({ visit, open: false });
  const chatOpen = chatState.visit === visit && chatState.open;
  const [editOpen, setEditOpen] = useState(false);
  const [noteFocus, setNoteFocus] = useState(0);
  const query = useQuery({
    queryKey: getDealDetailQueryKey(workspaceId, dealId),
    queryFn: () => getDealById(dealId),
    enabled: scope !== null,
    retry: false,
  });
  const health = useQuery({
    queryKey: getDealSalesHealthQueryKey(workspaceId, dealId),
    queryFn: () => getDealSalesHealth(dealId),
    enabled: scope !== null,
    retry: false,
  });
  // Win probability is computed on read: the query refetches together with
  // the deal itself, so the heuristic never goes stale on the card.
  const winProbability = useQuery({
    queryKey: getDealWinProbabilityQueryKey(workspaceId, dealId),
    queryFn: () => getDealWinProbability(dealId),
    enabled: scope !== null,
    retry: false,
  });
  const refetchWinProbability = winProbability.refetch;
  // Any deal update (stage move, amount edit, new note) bumps `updatedAt`:
  // refetch the probability once per observed change. The first observation
  // only records the stamp — the initial query fetch already covers it.
  const probabilityStamp = useRef<string | null | undefined>(undefined);
  const dealUpdatedAt = query.data?.updatedAt;
  useEffect(() => {
    const stamp = dealUpdatedAt ?? null;
    if (probabilityStamp.current === undefined) {
      probabilityStamp.current = stamp;
      return;
    }
    if (stamp === probabilityStamp.current) return;
    probabilityStamp.current = stamp;
    void refetchWinProbability();
  }, [dealUpdatedAt, refetchWinProbability]);
  const tasks = useTasks({ dealId });
  const notes = useNotes({ dealId });
  const meetings = useMeetings({ dealId });
  const documents = useDocuments({ dealId });
  const files = useEntityFiles('deal', dealId);
  const history = useDealHistory(dealId, query.data?.activities);
  useEffect(() => {
    const legacy = params.get('tab');
    if (!['details', 'stage-history', 'chat'].includes(legacy ?? '')) return;
    const next = new URLSearchParams(params);
    if (legacy === 'stage-history') {
      next.set('tab', 'history');
      next.set('historyFilter', 'stages');
    } else next.delete('tab');
    setParams(next, { replace: true });
  }, [params, setParams]);
  useEffect(() => {
    if (noteFocus && params.get('tab') === 'notes')
      document.querySelector<HTMLTextAreaElement>('[data-testid="entity-note-composer"]')?.focus();
  }, [noteFocus, params]);
  useWSChannel<DealStageChangedEvent>('/ws/deals', {
    enabled: scope !== null,
    onMessage: (event) => {
      if (
        event.type === 'deal.stage.changed' &&
        event.workspaceId === workspaceId &&
        event.dealId === dealId
      ) {
        void query.refetch();
        void health.refetch();
        void winProbability.refetch();
        history.refetch();
      }
    },
  });
  const openTab = (tab: string): void => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next);
  };
  // Navigate actions own no endpoint: `advance_stage` points at the stepper
  // button the user must confirm, `fill_qualification` at the deal chat.
  const navigateNextStep = (action: DealNextStepAction): void => {
    if (action.type === 'advance_stage') {
      const target = action.meta.targetStage;
      if (target === null) return;
      const button = document.querySelector<HTMLElement>(`[data-testid="deal-stage-${target}"]`);
      button?.focus();
      return;
    }
    setChatState({ visit, open: true });
  };
  if (query.isLoading)
    return (
      <main className="entity-detail-loading">
        <Skeleton active />
      </main>
    );
  if (query.error || !query.data)
    return (
      <main className="entity-detail-loading">
        <ErrorState title={t('crm.deal_card.error_title')} onRetry={() => void query.refetch()} />
      </main>
    );
  const deal = query.data;
  return (
    <ErrorBoundary>
      <EntityDetailLayout
        header={
          <DealDetailHeader
            deal={deal}
            health={health.data}
            onChat={() => setChatState({ visit, open: true })}
            onEdit={() => {
              setEditOpen(true);
            }}
            onNote={() => {
              openTab('notes');
              setNoteFocus((value) => value + 1);
            }}
            onHistory={() => openTab('history')}
          />
        }
        sidebar={
          <DealSidebar
            deal={deal}
            health={health.data}
            history={history.items}
            tasks={tasks.data ?? []}
            winProbability={winProbability.data}
          />
        }
        main={
          <EntityTabs
            items={[
              {
                key: 'overview',
                label: t('crm.entity_detail.overview'),
                content: (
                  <div className="deal-overview-sections">
                    <DealNextStepCard dealId={dealId} onNavigate={navigateNextStep} />
                    <DealWhatIfSimulator dealId={dealId} />
                    <DealOverview
                      dealId={dealId}
                      dueAt={deal.nextActivityDue}
                      tasks={tasks.data ?? []}
                      meetings={meetings.data ?? []}
                      history={history.items}
                    />
                  </div>
                ),
              },
              {
                key: 'tasks',
                label: t('crm.entity_detail.tasks'),
                count: tasks.data?.length ?? 0,
                content: <EntityTasksTab dealId={dealId} />,
              },
              {
                key: 'meetings',
                label: t('crm.entity_detail.meetings'),
                count: meetings.data?.length ?? 0,
                content: <EntityMeetingsTab dealId={dealId} />,
              },
              {
                key: 'notes',
                label: t('crm.entity_detail.notes'),
                count: notes.data?.length ?? 0,
                content: <EntityNotesTab dealId={dealId} />,
              },
              {
                key: 'documents',
                label: t('crm.entity_detail.documents'),
                count: documents.documents.length,
                content: <EntityDocumentsTab dealId={dealId} />,
              },
              {
                key: 'files',
                label: t('crm.entity_detail.files'),
                count: files.data?.length ?? 0,
                content: <EntityFilesTab dealId={dealId} />,
              },
              {
                key: 'history',
                label: t('crm.entity_detail.history'),
                count: history.items.length,
                content: (
                  <DealHistoryTab
                    items={history.items}
                    loading={history.isLoading}
                    error={history.error}
                    onRetry={history.refetch}
                  />
                ),
              },
            ]}
          />
        }
      />
      <Drawer
        data-testid="deal-chat-drawer"
        rootClassName="contact-chat-drawer"
        title={t('crm.deal_card.chat_mobile_title')}
        placement="right"
        size="min(760px, 100vw)"
        open={chatOpen}
        closable={{ 'aria-label': t('crm.deal_card.close_chat') }}
        onClose={() => setChatState({ visit, open: false })}
        styles={{ body: { padding: 0, overflow: 'hidden' } }}
      >
        {chatOpen ? <DealChatPanel scope={scope} dealId={dealId} /> : null}
      </Drawer>
      {editOpen ? <DealFormModal open deal={deal} onClose={() => setEditOpen(false)} /> : null}
    </ErrorBoundary>
  );
}
import { ErrorState } from '../../../shared/ui/ErrorState.js';
